import styled from 'styled-components';
import WithDirection from '@iso/lib/helpers/rtl';

const SensorDetailPage = styled.div`
  width: 100%;
  height: auto; /* Changed from 100% to auto for modal compatibility */
  max-height: 80vh; /* Add max height for modal */
  overflow-y: auto; /* Add scroll if content exceeds modal height */
  padding: 0; /* Remove default padding in modal context */
  
  /* Modal-specific adjustments */
  &.modal-view {
    height: auto;
    max-height: 75vh;
    padding: 8px;
    
    .ant-card {
      margin-bottom: 12px; /* Reduce spacing between cards */
    }
    
    .map-container {
      margin-bottom: 16px;
      
      /* Modal-specific slider positioning - less intrusive */
      .slider-overlay {
        position: absolute;
        top: 12px;
        left: 12px;
        right: auto;
        bottom: auto;
        width: 220px;
        padding: 12px;
        opacity: 0.9;
        backdrop-filter: blur(8px);
        
        /* Make it more subtle in modal */
        &:hover {
          opacity: 1;
        }
      }
    }
    
    .button-group {
      position: sticky;
      bottom: 0;
      background: white;
      padding: 12px 0;
      margin-top: 16px;
      border-top: 1px solid #f0f0f0;
      z-index: 10;
    }
  }
  
  .button-group {
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    gap: 10px;
  }

  .btn-success {
    background-color: #0B2A1B !important;
    border-color: #0B2A1B;
    height: 40px;
    border-radius: 8px;
    &:hover {
      opacity: 0.8;
    }
  }

  /* Compact card styling for modal */
  .ant-card {
    border-radius: 8px !important; /* Smaller radius for modal */
    border: 1px solid #f0f0f0;
    margin-bottom: 16px; /* Add consistent spacing */
    
    &:hover {
      border-color: #0B2A1B;
      box-shadow: 0 2px 8px rgba(11, 42, 27, 0.1); /* Reduced shadow for modal */
    }
  }
  
  .ant-card-head {
    border-radius: 8px 8px 0 0 !important;
    padding: 12px 16px; /* Reduce header padding */
    min-height: auto;
    
    .ant-card-head-title {
      font-size: 16px; /* Smaller title for modal */
      font-weight: 600;
    }
  }

  .ant-card-body {
    border-radius: 0 0 8px 8px !important;
    padding: 16px; /* Consistent padding */
  }
  
  .ant-card-small .ant-card-body {
    padding: 12px; /* Smaller padding for small cards */
  }

  /* Map specific styles - optimized for modal and matching ActivityWrapper */
  .leaflet-container {
    height: 100%;
    width: 100%;
    border-radius: 8px !important; /* Smaller radius */
  }

  .map-container {
    height: 100%; /* Fixed height for modal context */
    width: 100%;
    position: relative;
    margin-bottom: 16px;
    border-radius: 12px; /* Rounded container - matching ActivityWrapper */
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08); /* Added shadow for depth - matching ActivityWrapper */

    .leaflet-map {
      height: 100%;
      width: 100%;
      border: 2px solid #d9d9d9;
      border-radius: 12px; /* Made all corners rounded - matching ActivityWrapper */
    }

    .loading-overlay {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(255, 255, 255, 0.9);
      padding: 20px;
      border-radius: 8px;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
      z-index: 1000;
      display: flex;
      align-items: center;
      gap: 12px;
      font-family: "Inter";
      font-weight: 500;
      font-size: 14px;
      color: #101828;
    }

    /* Map mode toggle with green theme - matching ActivityWrapper with proper positioning */
    .map-mode-toggle {
      position: absolute;
      bottom: 10px;
      left: 10px;
      background: #fff;
      padding: 10px;
      border-radius: 6px;
      box-shadow: 0 1px 4px rgba(0,0,0,0.2);
      z-index: 1000;
      
      .ant-radio-group {
        display: flex;
        
        .ant-radio-button-wrapper {
          background-color: #fff;
          border-color: #0B2A1B;
          color: #0B2A1B;
          font-weight: 500;
          font-size: 12px;
          height: 32px;
          display: flex;
          align-items: center;
          transition: all 0.3s ease;
          
          &:hover {
            background-color: rgba(11, 42, 27, 0.05);
            border-color: #0B2A1B;
            color: #0B2A1B;
          }
          
          &.ant-radio-button-wrapper-checked {
            background-color: #0B2A1B;
            border-color: #0B2A1B;
            color: #fff;
            box-shadow: -1px 0 0 0 #0B2A1B;
            
            &:hover {
              background-color: #0B2A1B;
              opacity: 0.9;
            }
            
            &::before {
              background-color: #0B2A1B;
            }
          }
          
          &:first-child {
            border-radius: 4px 0 0 4px;
            border-left-color: #0B2A1B;
          }
          
          &:last-child {
            border-radius: 0 4px 4px 0;
          }
          
          &:not(:first-child)::before {
            background-color: #0B2A1B;
          }
        }
      }
    }

    /* Enhanced slider overlay styles - matching ActivityWrapper exactly */
    .slider-overlay {
      position: absolute;
      bottom: 20px;
      right: 20px;
      width: 280px;
      background-color: rgba(255, 255, 255, 0.95);
      border-radius: 12px; /* Made more rounded - matching ActivityWrapper */
      padding: 20px; /* Increased padding - matching ActivityWrapper */
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15); /* Enhanced shadow - matching ActivityWrapper */
      backdrop-filter: blur(8px);
      border: 1px solid rgba(255, 255, 255, 0.3);
      z-index: 1000;
      transition: all 0.3s ease;

      .slider-time-range {
        font-size: 11px;
        color: #666;
        font-weight: 500;
        margin-bottom: 12px;
        text-align: center;
      }

      .slider-time-labels {
        display: flex;
        justify-content: space-between;
        font-size: 10px;
        color: #888;
        margin-top: 8px;
      }

      /* Custom slider styling with green theme - matching ActivityWrapper exactly */
      .ant-slider {
        margin: 12px 0;
        
        .ant-slider-rail {
          background-color: #f0f0f0;
          height: 4px;
        }

        .ant-slider-track {
          background-color: #0B2A1B;
          height: 4px;
        }

        .ant-slider-handle {
          border-color: #0B2A1B;
          background-color: #fff;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
          width: 14px;
          height: 14px;

          &:hover {
            border-color: #0B2A1B;
          }

          &:focus {
            border-color: #0B2A1B;
            box-shadow: 0 0 0 2px rgba(11, 42, 27, 0.2);
          }
        }

        &:hover .ant-slider-track {
          background-color: #0B2A1B;
        }

        &:hover .ant-slider-handle:not(.ant-tooltip-open) {
          border-color: #0B2A1B;
        }
      }
    }
  }

  /* Primary button styling */
  .ant-btn-primary {
    background-color: #0B2A1B !important;
    border-color: #0B2A1B !important;
    border-radius: 6px; /* Smaller radius for modal */
    height: 36px; /* Smaller height for modal */
    
    &:hover, &:focus {
      background-color: rgba(11, 42, 27, 0.8) !important;
      border-color: rgba(11, 42, 27, 0.8) !important;
    }
  }

  /* Compact form controls for modal - matching ActivityWrapper styling */
  .ant-select-selector {
    border-radius: 8px !important;
    border-color: #d9d9d9 !important;
    
    &:hover {
      border-color: #0B2A1B !important;
    }
  }

  .ant-select-focused .ant-select-selector {
    border-color: #0B2A1B !important;
    box-shadow: 0 0 0 2px rgba(11, 42, 27, 0.1) !important;
  }

  .ant-picker {
    border-radius: 8px !important;
    border-color: #d9d9d9 !important;
    
    &:hover {
      border-color: #0B2A1B !important;
    }
  }

  .ant-picker-focused {
    border-color: #0B2A1B !important;
    box-shadow: 0 0 0 2px rgba(11, 42, 27, 0.1) !important;
  }

  /* Map control overrides - matching ActivityWrapper */
  .leaflet-control-zoom {
    margin-bottom: 35px !important;
    
    .leaflet-control-zoom-in,
    .leaflet-control-zoom-out {
      background-color: white;
      border: 1px solid #d9d9d9;
      color: #101828;
      
      &:hover {
        background-color: rgba(11, 42, 27, 0.05);
        border-color: #0B2A1B;
      }
    }
  }

  .leaflet-control-layers {
    .leaflet-control-layers-toggle {
      background-color: white;
      border: 1px solid #d9d9d9;
      
      &:hover {
        background-color: rgba(11, 42, 27, 0.05);
      }
    }
  }

  /* Selected sensor type card */
  .selected-sensor-type {
    border: 2px solid #0B2A1B !important;
    box-shadow: 0 2px 8px rgba(11, 42, 27, 0.2) !important;
  }

  /* Current values styling */
  .current-value-title,
  .current-value-title.ant-typography,
  .ant-typography.current-value-title {
    color: #0B2A1B !important;
    font-size: 14px !important; /* Smaller font for modal */
  }

  .ant-card-small .ant-typography h3 {
    color: #0B2A1B !important;
    font-size: 16px !important;
    margin-bottom: 8px !important;
  }

  .ant-statistic-content-value {
    color: #0B2A1B !important;
    font-size: 20px !important; /* Smaller statistic values */
  }

  /* Table styling - more compact for modal */
  .ant-table {
    border-radius: 6px !important;
    overflow: hidden;
    
    .ant-table-thead > tr > th {
      background-color: #fafafa;
      padding: 8px 12px; /* Reduce padding */
      font-size: 13px;
    }
    
    .ant-table-tbody > tr > td {
      padding: 8px 12px; /* Reduce padding */
      font-size: 13px;
    }
    
    .ant-table-tbody > tr:hover > td {
      background-color: rgba(11, 42, 27, 0.05) !important;
    }
  }

  /* Pagination - more compact */
  .ant-pagination {
    margin-top: 12px;
    
    .ant-pagination-item {
      min-width: 28px;
      height: 28px;
      line-height: 26px;
    }
    
    .ant-pagination-item-active {
      background-color: #0B2A1B !important;
      border-color: #0B2A1B !important;
    }
  }

  /* Loading spinner - matching ActivityWrapper */
  .ant-spin-dot-item {
    background-color: #0B2A1B !important;
  }

  /* Responsive adjustments for modal */
  @media only screen and (max-width: 768px) {
    &.modal-view {
      padding: 4px;
      max-height: 70vh;
      
      .map-container .slider-overlay {
        position: absolute;
        top: 8px;
        left: 8px;
        right: auto;
        bottom: auto;
        width: 180px;
        padding: 10px;
        font-size: 10px;
      }
    }
    
    .button-group {
      flex-direction: column;
      align-items: stretch;
      gap: 8px;
      padding: 8px 0;
    }
    
    .map-container {
      height: 250px; /* Smaller map height on mobile */
      
      /* Default mobile slider positioning (non-modal) - matching ActivityWrapper */
      .slider-overlay {
        width: calc(100% - 40px);
        right: 20px;
        left: 20px;
        bottom: 8px;
        padding: 16px;
      }

      .map-mode-toggle {
        left: 50%;
        right: auto;
        transform: translateX(-50%);
        bottom: 5px;
      }
    }
    
    .ant-card-body {
      padding: 12px !important;
    }
  }

  @media only screen and (max-width: 425px) {
    &.modal-view {
      .map-container .slider-overlay {
        width: 160px;
        padding: 8px;
        
        .slider-time-range {
          font-size: 9px;
          margin-bottom: 8px;
        }
        
        .slider-time-labels {
          font-size: 8px;
        }
      }
    }
    
    .map-container {
      height: 200px;
      
      .slider-overlay {
        width: calc(100% - 20px);
        right: 10px;
        left: 10px;
        padding: 12px;
      }

      .map-mode-toggle {
        width: calc(100% - 20px);
        left: 10px;
        right: 10px;
        transform: none;
      }
    }
    
    .ant-card-head {
      padding: 8px 12px;
      
      .ant-card-head-title {
        font-size: 14px;
      }
    }
  }

  @media only screen and (max-width: 375px) {
    .map-container {
      .slider-overlay {
        width: calc(100% - 20px);
        right: 10px;
        left: 10px;
        padding: 12px;
      }

      .map-mode-toggle {
        width: calc(100% - 20px);
        left: 10px;
        right: 10px;
        transform: none;
      }
    }
  }

  /* Additional responsive breakpoints for larger screens - matching ActivityWrapper */
  @media only screen and (width: 1440px),
         only screen and (width: 1024px),
         only screen and (width: 768px) {
    .map-container {
      height: 70vh;
      margin-bottom: 28px;
    }
  }
`;

export default WithDirection(SensorDetailPage);